import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { LuArrowLeft, LuCopy, LuCheck, LuExternalLink, LuWebhook, LuShield } from "react-icons/lu";
import { FaWhatsapp } from "react-icons/fa";
import { API } from "../App";

const WhatsAppConfigPage = ({ auth }) => {
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [copied, setCopied] = useState({ url: false, token: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWebhookInfo();
  }, []);

  const fetchWebhookInfo = async () => {
    try {
      const response = await fetch(`${API}/whatsapp/webhook-info`);
      if (response.ok) {
        const data = await response.json();
        setWebhookInfo(data);
      }
    } catch (error) {
      toast.error("Error al cargar información del webhook");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ ...copied, [field]: true });
      toast.success("Copiado al portapapeles");
      setTimeout(() => setCopied({ ...copied, [field]: false }), 2000);
    } catch (error) {
      toast.error("Error al copiar");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" className="rounded-xl" data-testid="back-btn">
              <LuArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
              <FaWhatsapp className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg text-foreground">Configuración WhatsApp</h1>
              <p className="text-xs text-muted-foreground">Webhook para WhatsApp Business API</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-8 space-y-6">
        {/* Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="glass-card border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <LuWebhook className="w-6 h-6 text-green-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-foreground">Webhook Activo</h3>
                  <p className="text-sm text-muted-foreground">
                    Tu servidor está listo para recibir mensajes de WhatsApp
                  </p>
                </div>
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  Conectado
                </Badge>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Webhook URL */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <LuWebhook className="w-5 h-5 text-primary" />
                Callback URL
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Usa esta URL en la configuración de tu webhook en Meta for Developers
              </p>
              <div className="flex gap-2">
                <Input
                  value={webhookInfo?.webhook_url || ""}
                  readOnly
                  className="bg-muted/50 border-transparent font-mono text-sm"
                  data-testid="webhook-url"
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                  onClick={() => copyToClipboard(webhookInfo?.webhook_url, "url")}
                  data-testid="copy-url-btn"
                >
                  {copied.url ? <LuCheck className="w-4 h-4 text-green-500" /> : <LuCopy className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Verify Token */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <LuShield className="w-5 h-5 text-primary" />
                Verify Token
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Token de verificación para validar el webhook
              </p>
              <div className="flex gap-2">
                <Input
                  value={webhookInfo?.verify_token || ""}
                  readOnly
                  className="bg-muted/50 border-transparent font-mono text-sm"
                  data-testid="verify-token"
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                  onClick={() => copyToClipboard(webhookInfo?.verify_token, "token")}
                  data-testid="copy-token-btn"
                >
                  {copied.token ? <LuCheck className="w-4 h-4 text-green-500" /> : <LuCopy className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Instructions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="text-foreground">Instrucciones de Configuración</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-4">
                {webhookInfo?.instructions?.map((instruction, index) => (
                  <li key={index} className="flex gap-4">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                      {index + 1}
                    </span>
                    <span className="text-muted-foreground pt-1">{instruction.replace(/^\d+\.\s*/, "")}</span>
                  </li>
                ))}
              </ol>
              
              <div className="mt-6 pt-6 border-t border-border">
                <a
                  href="https://developers.facebook.com/apps/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary hover:underline"
                >
                  <LuExternalLink className="w-4 h-4" />
                  Ir a Meta for Developers
                </a>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Test Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="glass-card border-white/10 border-dashed">
            <CardContent className="p-6 text-center">
              <FaWhatsapp className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-foreground mb-2">
                ¿Todo configurado?
              </h3>
              <p className="text-muted-foreground mb-4">
                Envía un mensaje de WhatsApp al número configurado para probar la integración
              </p>
              <Badge variant="outline" className="border-green-500/30 text-green-400">
                Los mensajes entrantes serán procesados por tu agente activo
              </Badge>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default WhatsAppConfigPage;
